/**
 * Dynamic Product Hiding Based on Country Selection
 * 
 * This script handles dynamic hiding/showing of specific products in collections
 * when users change their country/currency through localization selectors.
 * 
 * Features:
 * - Listens for localization form submissions
 * - Dynamically shows/hides products based on country
 * - Supports multiple products and collections
 */

class LocalizationProductHider {
  constructor() {
    this.config = {
      // Configuration for products to hide per country
      countryRules: {
        'AU': { // Australia
          collections: {
            'sale': [
              '4787821412461', // set of 4 white
            ]
          }
        },
        'HK': { // Australia
          collections: {
            'sale': [
              // '4787812171885', // Example product id
            ]
          }
        },
        'ID': { // Australia
          collections: {
            'sale': [
              // '4787812171885', // Example product id
            ]
          }
        },
        'JP': { // Australia
          collections: {
            'sale': [
              // '4787812171885', // Example product id
            ]
          }
        },
        'MO': { // Australia
          collections: {
            'sale': [
              '4787812171885', // Example product id
            ]
          }
        },
        'NZ': { // Australia
          collections: {
            'sale': [
              // '4787812171885', // Example product id
            ]
          }
        },
        'SG': { // Australia
          collections: {
            'sale': [
              // '4787812171885', // Example product id
            ]
          }
        },
        'TW': { // Australia
          collections: {
            'sale': [
              // '4787812171885', // Example product id
            ]
          }
        },
        'TH': { // Australia
          collections: {
            'sale': [
              // '4787812171885', // Example product id
            ]
          }
        },
        // Add more countries and rules as needed
      }
    };
    
    this.currentCountry = this.getCurrentCountry();
    this.init();
  }

  init() {
    // console.log('[LocalizationProductHider] Initializing with country:', this.currentCountry);
    
    // Apply initial hiding based on current country
    this.applyProductVisibility();
    
    // Listen for localization form submissions
    this.setupLocalizationListeners();
    
    // Listen for custom events that might indicate country changes
    this.setupCustomEventListeners();
    
    // Watch for DOM changes in case products are loaded dynamically
    this.setupMutationObserver();
    
    // Monitor URL changes for SPAs or AJAX navigation
    this.setupURLChangeListener();
  }

  getCurrentCountry() {
    // Try multiple methods to get current country
    
    // Method 1: Check if country is available in window.themeVariables
    if (window.themeVariables && window.themeVariables.localization && window.themeVariables.localization.country) {
      return window.themeVariables.localization.country.iso_code;
    }
    
    // Method 2: Look for country in localization forms
    const localizationForms = document.querySelectorAll('form[action*="localization"]');
    for (const form of localizationForms) {
      const countryInput = form.querySelector('input[name="country_code"]');
      if (countryInput && countryInput.value) {
        return countryInput.value;
      }
    }
    
    // Method 3: Check currency and make educated guess
    const currency = this.getCurrentCurrency();
    if (currency === 'AUD') {
      return 'AU';
    }
    
    // Method 4: Look for country in page content
    const countrySelectors = document.querySelectorAll('[aria-current="true"]');
    for (const selector of countrySelectors) {
      const text = selector.textContent;
      if (text.includes('Australia') || text.includes('AUD')) {
        return 'AU';
      }
    }
    
    // Default fallback
    return null;
  }

  getCurrentCurrency() {
    // Try to get currency from various sources
    if (window.themeVariables && window.themeVariables.localization && window.themeVariables.localization.country) {
      return window.themeVariables.localization.country.currency.iso_code;
    }
    
    // Look for currency in page content
    const currencyElements = document.querySelectorAll('[data-currency], .money');
    for (const element of currencyElements) {
      const currency = element.getAttribute('data-currency') || element.textContent.match(/[A-Z]{3}/);
      if (currency) {
        return currency;
      }
    }
    
    return null;
  }

  setupLocalizationListeners() {
    // Listen for all localization form submissions
    const localizationForms = document.querySelectorAll('form[action*="localization"]');
    
    localizationForms.forEach(form => {
      form.addEventListener('submit', (event) => {
        
        // Get the new country from the form
        const formData = new FormData(form);
        const newCountry = formData.get('country_code');
        
        if (newCountry && newCountry !== this.currentCountry) {
          
          // Update current country
          this.currentCountry = newCountry;
          
          // If the form submission doesn't cause a page reload (AJAX),
          // we need to apply visibility changes immediately
          setTimeout(() => {
            this.applyProductVisibility();
          }, 100);
        }
      });
      
      // Also listen for button clicks within localization forms
      const submitButtons = form.querySelectorAll('button[type="submit"]');
      submitButtons.forEach(button => {
        button.addEventListener('click', (event) => {
          const newCountry = button.value || button.getAttribute('data-country');
          if (newCountry && newCountry !== this.currentCountry) {
            this.currentCountry = newCountry;
            
            // Apply changes immediately in case of AJAX submission
            setTimeout(() => {
              this.applyProductVisibility();
            }, 100);
          }
        });
      });
    });
  }

  setupCustomEventListeners() {
    // Listen for Shopify's cart and product events that might indicate country changes
    document.addEventListener('cart:updated', () => {
      const newCountry = this.getCurrentCountry();
      if (newCountry && newCountry !== this.currentCountry) {
        this.currentCountry = newCountry;
        this.applyProductVisibility();
      }
    });
    
    // Listen for variant changes that might affect country detection
    document.addEventListener('variant:changed', () => {
      const newCountry = this.getCurrentCountry();
      if (newCountry && newCountry !== this.currentCountry) {
        this.currentCountry = newCountry;
        this.applyProductVisibility();
      }
    });
    
    // Custom event for manual country changes
    document.addEventListener('localization:country:changed', (event) => {
      const newCountry = event.detail.country;
      if (newCountry && newCountry !== this.currentCountry) {
        this.currentCountry = newCountry;
        this.applyProductVisibility();
      }
    });
  }

  setupMutationObserver() {
    // Watch for changes in product lists (in case they're loaded dynamically)
    const observer = new MutationObserver((mutations) => {
      let shouldReapply = false;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          // Check if any product items were added
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.matches && (
                node.matches('.product-item') || 
                node.matches('[data-product-handle]') ||
                node.querySelector && node.querySelector('.product-item, [data-product-handle]')
              )) {
                shouldReapply = true;
              }
            }
          });
        }
      });
      
      if (shouldReapply) {
        this.applyProductVisibility();
      }
    });
    
    // Observe the main content area
    const mainContent = document.querySelector('main, #main, .main-content') || document.body;
    observer.observe(mainContent, {
      childList: true,
      subtree: true
    });
  }

  setupURLChangeListener() {
    // Monitor for URL changes that might indicate navigation
    let currentURL = window.location.href;
    
    const checkURLChange = () => {
      if (window.location.href !== currentURL) {
        currentURL = window.location.href;
        
        const newCountry = this.getCurrentCountry();
        if (newCountry && newCountry !== this.currentCountry) {
          this.currentCountry = newCountry;
          this.applyProductVisibility();
        }
      }
    };
    
    // Check for URL changes periodically
    setInterval(checkURLChange, 1000);
    
    // Also listen for popstate events
    window.addEventListener('popstate', checkURLChange);
  }

  applyProductVisibility() {
    if (!this.currentCountry) {
      this.showAllProducts();
      return;
    }
    
    const countryRules = this.config.countryRules[this.currentCountry];
    if (!countryRules) {
      this.showAllProducts();
      return;
    }
    
    // Get current collection handle
    const currentCollection = this.getCurrentCollectionHandle();
    if (!currentCollection) {
      return;
    }
    
    // Check if current collection has rules for this country
    const collectionRules = countryRules.collections[currentCollection];
    if (!collectionRules || !Array.isArray(collectionRules)) {
      return;
    }
      // Hide products based on rules
    collectionRules.forEach(productId => {
      this.hideProduct(productId);
    });
    
  }

  getCurrentCollectionHandle() {
    // Try multiple methods to get collection handle
    
    // Method 1: Check URL for collection
    const urlMatch = window.location.pathname.match(/\/collections\/([^\/]+)/);
    if (urlMatch) {
      return urlMatch[1];
    }
    
    return null;
  }  
  
  hideProduct(productId) {
    // Find hidden input elements with the specific product-id
    let hiddenInputs = document.querySelectorAll(`input[name="product-id"][value="${productId}"]`);
    
    // Fallback: if no inputs with name="product-id" found, try common alternative selectors
    if (hiddenInputs.length === 0) {
      // Try looking for variant ID inputs or other product identifiers
      hiddenInputs = document.querySelectorAll(`input[value="${productId}"]`);
    }
    
    if (hiddenInputs.length > 0) {
      hiddenInputs.forEach(input => {
        // Use closest() to find the parent product-item element
        const productItemElement = input.closest('product-item.product-item');
        
        if (productItemElement) {
          
          // Hide the product-item element
          productItemElement.style.display = 'none';
          productItemElement.setAttribute('data-country-hidden', 'true');
          productItemElement.setAttribute('data-hidden-for-country', this.currentCountry);
          productItemElement.setAttribute('data-hidden-product-id', productId);
          
          // Add class for CSS targeting
          productItemElement.classList.add('country-hidden');
          
        } else {
        }
      });
    } else {
    }
  }

  showAllProducts() {
    // Show all previously hidden products
    const hiddenProducts = document.querySelectorAll('[data-country-hidden="true"]');
    hiddenProducts.forEach(product => {
      
      product.style.display = '';
      product.removeAttribute('data-country-hidden');
      product.removeAttribute('data-hidden-for-country');
      product.removeAttribute('data-hidden-product-id');
      product.classList.remove('country-hidden');
    });
  }

  // Public method to manually trigger country change
  updateCountry(newCountry) {
    if (newCountry !== this.currentCountry) {
      this.currentCountry = newCountry;
      this.applyProductVisibility();
      
      // Dispatch custom event
      document.dispatchEvent(new CustomEvent('localization:country:changed', {
        detail: { country: newCountry }
      }));
    }
  }

  // Public method to add new rules dynamically
  addRule(country, collection, productIds) {
    if (!this.config.countryRules[country]) {
      this.config.countryRules[country] = { collections: {} };
    }
    
    if (!this.config.countryRules[country].collections[collection]) {
      this.config.countryRules[country].collections[collection] = [];
    }
    
    const ids = Array.isArray(productIds) ? productIds : [productIds];
    this.config.countryRules[country].collections[collection].push(...ids);
    
    // Reapply if this affects current context
    if (country === this.currentCountry) {
      this.applyProductVisibility();
    }
  }
}

// Initialize when DOM is ready
function initLocalizationProductHider() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.localizationProductHider = new LocalizationProductHider();
    });
  } else {
    window.localizationProductHider = new LocalizationProductHider();
  }
}

initLocalizationProductHider();

// Export for manual usage
window.LocalizationProductHider = LocalizationProductHider;
